import type { AdminUserBaby, AdminUserSummary } from '@/admin/adminApi';
import { ROLE_LABELS } from '@/admin/adminApi';
import { SENSITIVE_ACTIONS } from '@/admin/sensitiveActions';

/**
 * Mise en forme des comptes, ecrite **hors des composants**.
 *
 * Tout ce que cet ecran affiche d'un compte est une phrase, pas une donnee
 * brute : « 3 profils » et non `babyCount: 3`, « Jamais connecté » et non
 * `lastLoginAt: null`. Ces regles vivent ici pour deux raisons : elles se
 * testent sans rendu, et elles ne peuvent pas diverger entre la liste et la
 * fiche, qui les partagent.
 *
 * **Aucune information n'est portee par la seule couleur** : chaque etat est un
 * libelle. La teinte accompagne le mot, elle ne le remplace jamais — un ecran
 * d'administration lu en noir et blanc, ou par un lecteur d'ecran, doit dire la
 * meme chose.
 */

const DATE_TIME_FORMAT = new Intl.DateTimeFormat('fr-FR', {
  dateStyle: 'long',
  timeStyle: 'short',
});

const NUMBER_FORMAT = new Intl.NumberFormat('fr-FR');

/** Marque d'absence, partagee par tous les ecrans d'administration. */
export const ABSENT = '—';

/**
 * Horodatage ISO en date francaise lisible.
 *
 * Une chaine illisible rend le repli plutot que « Invalid Date » : le serveur
 * est cense rendre de l'ATOM, mais un ecran d'exploitation ne doit pas afficher
 * un message d'erreur JavaScript a la place d'une date.
 */
export const formatInstant = (iso: string | null, fallback: string = ABSENT): string => {
  if (iso === null || iso === '') {
    return fallback;
  }

  const date = new Date(iso);

  return Number.isNaN(date.getTime()) ? fallback : DATE_TIME_FORMAT.format(date);
};

/** Teinte d'accompagnement. Jamais seule porteuse d'une information. */
export type StateTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

export interface AccountState {
  readonly label: string;
  readonly tone: StateTone;
}

/**
 * Etats affiches a cote d'une adresse.
 *
 * L'ordre est fixe et commence par le plus lourd de consequence :
 * l'anonymisation change ce qu'on peut encore faire du compte, la verification
 * de l'adresse explique pourquoi une personne ne recoit rien, le role
 * d'administration dit qu'on regarde un compte qui voit ceux des autres.
 */
export const accountStates = (user: AdminUserSummary): readonly AccountState[] => {
  const states: AccountState[] = [];

  if (user.anonymized) {
    states.push({ label: 'Anonymisé', tone: 'danger' });
  }

  // Reversible, contrairement a l'anonymisation : un ton d'attention, pas de
  // danger. Le libelle porte le sens, la teinte l'accompagne.
  if (user.suspendedAt !== null) {
    states.push({ label: 'Suspendu', tone: 'warning' });
  }

  states.push(
    user.emailVerifiedAt === null
      ? { label: 'Adresse non vérifiée', tone: 'neutral' }
      : { label: 'Adresse vérifiée', tone: 'success' },
  );

  if (user.adminRole !== null) {
    states.push({ label: ROLE_LABELS[user.adminRole], tone: 'info' });
  }

  return states;
};

export const describeBabyCount = (count: number): string => {
  if (count <= 0) {
    return 'Aucun profil';
  }

  return count === 1 ? '1 profil' : `${NUMBER_FORMAT.format(count)} profils`;
};

export const describeSessionCount = (count: number): string => {
  if (count <= 0) {
    return 'Aucun appareil connecté';
  }

  return count === 1 ? '1 appareil connecté' : `${NUMBER_FORMAT.format(count)} appareils connectés`;
};

/** Roles de coparentalite (`BabyMemberRole`), dits en francais. */
const BABY_ROLE_LABELS: Readonly<Record<string, string>> = {
  owner: 'Titulaire',
  admin: 'Administrateur du profil',
  contributor: 'Contributeur',
  reader: 'Lecture seule',
};

/**
 * Un role inconnu est **affiche tel quel**, jamais masque ni traduit en
 * « autre ». Si le serveur gagnait un role, l'ecran doit le montrer plutot que
 * de le cacher derriere un mot generique.
 */
export const babyRoleLabel = (role: string | null): string => {
  if (role === null || role === '') {
    return 'Rôle inconnu';
  }

  return BABY_ROLE_LABELS[role] ?? role;
};

/**
 * Motif de suspension en toutes lettres.
 *
 * Le libelle vient de la liste fermee de l'action (`SENSITIVE_ACTIONS.suspend`),
 * qui est le miroir du serveur : un code que cet ecran ne connait pas est
 * **affiche tel quel**, jamais masque — c'est ce qui le rend lisible apres une
 * evolution de la liste.
 */
export const suspensionReasonLabel = (code: string | null): string => {
  if (code === null || code === '') {
    return ABSENT;
  }

  return SENSITIVE_ACTIONS.suspend.reasons.find((reason) => reason.code === code)?.label ?? code;
};

/** Un profil sans prenom existe en base : la fiche ne doit pas afficher un vide. */
export const babyName = (baby: AdminUserBaby): string =>
  baby.firstName === null || baby.firstName.trim() === '' ? 'Profil sans prénom' : baby.firstName;

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

/**
 * Valeur de `total` quand le serveur n'en a pas rendu (`adminApi.readPage`).
 *
 * Elle ne doit **jamais** atteindre l'ecran telle quelle : « -1 compte » serait
 * un total invente a l'envers. C'est la meme regle que le reste du dossier —
 * on n'invente pas un nombre —, appliquee au cas ou le nombre manque.
 */
export const TOTAL_UNKNOWN = -1;

export const totalIsKnown = (total: number): boolean => Number.isFinite(total) && total >= 0;

export interface PageState {
  readonly total: number;
  readonly page: number;
  readonly perPage: number;
  readonly itemCount: number;
}

export const pageCount = ({ total, perPage }: PageState): number =>
  perPage <= 0 ? 1 : Math.max(1, Math.ceil(total / perPage));

const accountCountLabel = (total: number): string => {
  if (total === 0) {
    return 'Aucun compte';
  }

  return total === 1 ? '1 compte' : `${NUMBER_FORMAT.format(total)} comptes`;
};

/**
 * Phrase de pagination.
 *
 * Deux redactions, parce qu'il y a deux situations reellement differentes : le
 * serveur a compte, ou il n'a pas compte. La seconde le **dit**, au lieu de
 * laisser croire que la page affichee est tout ce qui existe.
 */
export const describePage = (state: PageState): string => {
  if (!totalIsKnown(state.total)) {
    const shown =
      state.itemCount === 1 ? '1 compte affiché' : `${NUMBER_FORMAT.format(state.itemCount)} comptes affichés`;

    return `Page ${NUMBER_FORMAT.format(state.page)} · ${shown} · nombre total non communiqué par le serveur`;
  }

  return `${accountCountLabel(state.total)} · page ${NUMBER_FORMAT.format(state.page)} sur ${NUMBER_FORMAT.format(pageCount(state))}`;
};

/**
 * Y a-t-il une page suivante ?
 *
 * Sans total, la seule deduction honnete est « une page pleine peut en cacher
 * une autre » : on propose donc la suivante, quitte a ce qu'elle soit vide, et
 * l'ecran le dira. L'inverse — masquer le bouton — rendrait des comptes
 * inatteignables.
 */
export const hasNextPage = (state: PageState): boolean =>
  totalIsKnown(state.total)
    ? state.page * state.perPage < state.total
    : state.itemCount >= state.perPage && state.itemCount > 0;
